<template>
  <div class="reco-page client-page">
    <div class="reco-page__content">
      <header class="client-page__header">
        <h1 class="client-page__title">Mes recommandations</h1>
        <p class="client-page__intro">
          Vos priorités patrimoniales, classées par ordre d’importance. Consultez le détail,
          puis échangez avec votre conseiller pour passer à l’exécution.
        </p>
      </header>

      <!-- Offre Duo : onglets Conjoint 1 / Conjoint 2 / Couple -->
      <div v-if="typeOffre === 'duo'" class="reco-duo-tabs" role="tablist" aria-label="Recommandations offre duo">
        <button
          type="button"
          class="reco-duo-tab"
          :class="{ active: activeDuoTab === 'conjoint1' }"
          @click="activeDuoTab = 'conjoint1'"
        >
          Conjoint 1
        </button>
        <button
          type="button"
          class="reco-duo-tab"
          :class="{ active: activeDuoTab === 'conjoint2' }"
          @click="activeDuoTab = 'conjoint2'"
        >
          Conjoint 2
        </button>
        <button
          type="button"
          class="reco-duo-tab"
          :class="{ active: activeDuoTab === 'couple' }"
          @click="activeDuoTab = 'couple'"
        >
          Couple
        </button>
      </div>

      <div v-if="errorMessage" class="client-alert client-alert--error reco-alert">
        <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
        {{ errorMessage }}
      </div>

      <div v-if="loadingAnalysis" class="reco-loading">
        <div class="reco-loading__spinner" aria-hidden="true" />
        <p class="reco-loading__text">Chargement des recommandations…</p>
      </div>

      <div v-else-if="!isAnalysisCompleted" class="reco-empty">
        <ClientJourneyEmptyState
          :title="recoEmptyTitle"
          :message="recoEmptyMessage"
          :active-step="recoEmptyStep"
          :primary-to="recoEmptyPrimaryTo"
          :primary-label="recoEmptyPrimaryLabel"
          secondary-to="/analyse-patrimoniale/contact"
          secondary-label="Contacter mon conseiller"
        />
      </div>

      <div v-else-if="recommendationCards.length === 0" class="reco-empty">
        <ClientJourneyEmptyState
          title="Recommandations bientôt disponibles"
          message="Votre analyse est publiée. Les recommandations personnalisées vont apparaître ici dès qu’elles seront finalisées par votre conseiller."
          :active-step="3"
          primary-to="/analyse-patrimoniale/analyses-sups"
          primary-label="Voir mes résultats"
          secondary-to="/analyse-patrimoniale/contact"
          secondary-label="Contacter mon conseiller"
        />
      </div>

      <div v-else class="reco-grid">
        <TransitionGroup name="card-list">
          <div
            v-for="(card, index) in recommendationCards"
            :key="card.id"
            class="reco-card"
            @click="expandedId = expandedId === card.id ? null : card.id"
          >
            <div class="reco-card__header">
              <div class="reco-card__title-row">
                <h3 class="reco-card__title">{{ card.title }}</h3>
                <span class="reco-card__badge" :class="'reco-card__badge--' + card.status">
                  {{ statusLabel(card.status) }}
                </span>
        </div>
              <p v-if="card.description" class="reco-card__description">{{ card.description }}</p>
              <p class="reco-card__date">
                <CalendarIcon class="reco-card__date-icon" />
                {{ card.dateLabel }}
              </p>
            </div>
            <div class="reco-card__progress-wrap">
              <div class="reco-card__progress-bar">
                <div class="reco-card__progress-fill" :style="{ width: card.progress + '%' }" />
              </div>
              <span class="reco-card__progress-text">{{ card.progress }}%</span>
            </div>
            <div class="reco-card__actions">
              <button
                type="button"
                class="client-btn client-btn--primary"
                @click.stop="expandedId = expandedId === card.id ? null : card.id"
              >
                <EyeIcon class="reco-card__btn-icon" />
                {{ expandedId === card.id ? 'Masquer le détail' : 'Voir le détail' }}
              </button>
              <router-link
                to="/analyse-patrimoniale/contact"
                class="client-btn client-btn--ghost"
                @click.stop
              >
                Contacter mon conseiller
              </router-link>
            </div>
            <Transition name="expand">
              <div v-if="expandedId === card.id && card.detailHtml" class="reco-card__detail">
                <div class="reco-card__detail-inner" v-html="card.detailHtml" />
              </div>
            </Transition>
          </div>
        </TransitionGroup>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  ExclamationCircleIcon,
  CalendarIcon,
  EyeIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';
import { getErrorMessage } from '@/utils/errorMessages';
import ClientJourneyEmptyState from '@/components/patrimoine/ClientJourneyEmptyState.vue';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { isAuthenticatedSession } from '@/utils/sessionAuth';
import { syncEmployeProfileFromApi } from '@/utils/permissions';
import { isAnalysePublished } from '@/utils/dossierState';

const router = useRouter();

const loadingAnalysis = ref(true);
const errorMessage = ref('');
const rawAnalysis = ref(null);
const rawAnalysisConjoint1 = ref(null);
const rawAnalysisConjoint2 = ref(null);
const duoMenage = ref(null);
const expandedId = ref(null);
const typeOffre = ref('persona'); // 'persona' | 'duo'
const activeDuoTab = ref('conjoint1'); // 'conjoint1' | 'conjoint2' | 'couple'

const todoActions = ref([]);
const todoDone = computed(() => todoActions.value.filter((a) => a.statut === 'terminee'));
const todoProgress = computed(() => {
  const total = todoActions.value.length;
  if (!total) return 0;
  return Math.round((100 * todoDone.value.length) / total);
});

function normalizeLatestDetails(raw) {
          if (!raw) return null;
          if (raw.details) return raw.details;
          if (Array.isArray(raw) && raw.length > 0) return raw[0];
  if (raw.results && raw.results.length > 0) return raw.results[0];
          return raw;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function statusLabel(status) {
  const map = { 'a_faire': 'À faire', 'en_cours': 'En cours', 'termine': 'Terminé' };
  return map[status] || status;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCurrency(value) {
  if (value == null || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)} FCFA`;
}

/** Convertit le markdown (### titres, **gras**) en HTML pour un texte homogène sans dièses/étoiles bruts */
function renderMarkdownToHtml(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  const escape = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  const lines = text.split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) { out.push('<br/>'); continue; }
    const h3Match = trimmed.match(/^###\s*(.*)$/);
    if (h3Match) {
      out.push('<h3 class="reco-rendered-heading">' + escape(h3Match[1]) + '</h3>');
      continue;
    }
    const withBold = escape(trimmed).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    out.push('<p class="reco-rendered-p">' + withBold + '</p>');
  }
  return sanitizeHtml(out.join(''));
}

/** Tableau allocation recommandée selon l'horizon/âge */
function buildHorizonAllocationTable(latest) {
  const repartition = latest?.repartition;
  if (!Array.isArray(repartition) || repartition.length === 0) return '';

  const rows = repartition
    .map((item) => {
      const classe = item?.classe || item?.label || '—';
      const recommande = item?.recommande || '—';
      const description = item?.description || '—';
      return `
        <tr>
          <td class="reco-table-cat">${escapeHtml(classe)}</td>
          <td>${escapeHtml(recommande)}</td>
          <td>${escapeHtml(description)}</td>
        </tr>`;
    })
    .join('');

  return `
    <div class="reco-detail-section">
      <h4>Allocation recommandée selon votre profil</h4>
      <div class="reco-table-wrap">
        <table class="reco-table">
          <thead>
            <tr>
              <th>Classe d'actifs</th>
              <th>Allocation recommandée</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>`;
}

/** Tableau répartition de l'épargne par catégorie */
function buildRepartitionEpargneTable(latest) {
  const rep = latest?.repartition_epargne;
  if (!Array.isArray(rep) || rep.length === 0) return '';
  const totalEpargne = latest?.charges_details?.details?.epargne?.total ?? latest?.charges_details?.epargne?.total;
  const range = (min, max) => {
    if (min != null && max != null && min === max) return formatCurrency(min);
    if (min != null && max != null) return `${formatCurrency(min)} – ${formatCurrency(max)}`;
    if (min != null) return formatCurrency(min);
    if (max != null) return formatCurrency(max);
    return '—';
  };
  const rows = rep
    .map((cat) => {
      const label = cat.categorie || cat.label || '—';
      const montant = range(cat.montant_categorie_min, cat.montant_categorie_max);
      return `<tr><td class="reco-table-cat">${escapeHtml(label)}</td><td>${escapeHtml(montant)}</td></tr>`;
    })
    .join('');
  const totalRow = totalEpargne != null
    ? `<tr class="reco-table-total"><td>Montant mensuel de l'épargne</td><td>${escapeHtml(formatCurrency(totalEpargne))}</td></tr>`
    : '';
  return `
    <div class="reco-detail-section">
      <h4>Répartition de l'épargne par catégorie</h4>
      <div class="reco-table-wrap">
        <table class="reco-table">
          <thead><tr><th>Catégorie</th><th>Montant (mensuel)</th></tr></thead>
          <tbody>${rows}${totalRow}</tbody>
        </table>
      </div>
    </div>`;
}

function buildCoupleDetailHtml(a1, a2, menage) {
  const parts = [];

  const addBloc = (title, analyse) => {
    if (!analyse) return;
    const createdAt = analyse.created_at ?? analyse.date_creation ?? analyse.createdAt;
    const dateLabel = formatDate(createdAt);
    const iaText = analyse.recommandation_ia_modifie || analyse.recommandation_ia;
    const rendered = iaText ? renderMarkdownToHtml(iaText) : '';
    parts.push(
      `<div class="reco-detail-section reco-rendered">
        <h4>${escapeHtml(title)} <span class="reco-duo-mini-date">(${escapeHtml(dateLabel)})</span></h4>
        ${rendered ? `<div class="reco-rendered-body">${rendered}</div>` : `<p>Aucune recommandation disponible.</p>`}
      </div>`
    );
  };

  addBloc('Recommandations — Conjoint 1', a1);
  addBloc('Recommandations — Conjoint 2', a2);

  const patrimoine = menage?.patrimoine;
  const budget = menage?.budget;
  if (patrimoine || budget) {
    parts.push('<div class="reco-detail-section"><h4>Synthèse ménage (Couple)</h4><ul>');
    if (budget) {
      parts.push(`<li><strong>Revenus annuels :</strong> ${escapeHtml(formatCurrency(budget.revenus_annuel))}</li>`);
      parts.push(`<li><strong>Dépenses annuelles :</strong> ${escapeHtml(formatCurrency(budget.depenses_annuel))}</li>`);
      parts.push(`<li><strong>Solde annuel :</strong> ${escapeHtml(formatCurrency(budget.solde_annuel))}</li>`);
    }
    if (patrimoine) {
      parts.push(`<li><strong>Actif total :</strong> ${escapeHtml(formatCurrency(patrimoine.total_actif))}</li>`);
      parts.push(`<li><strong>Passif total :</strong> ${escapeHtml(formatCurrency(patrimoine.total_passif))}</li>`);
      parts.push(`<li><strong>Valeur nette :</strong> ${escapeHtml(formatCurrency(patrimoine.valeur_patrimoine))}</li>`);
    }
    parts.push('</ul></div>');
  }

  return parts.filter(Boolean).join('') || getDefaultAnalysisDetail();
}

const activeAnalysis = computed(() => {
  if (typeOffre.value !== 'duo') return rawAnalysis.value;
  if (activeDuoTab.value === 'conjoint2') return rawAnalysisConjoint2.value;
  if (activeDuoTab.value === 'couple') return null;
  return rawAnalysisConjoint1.value;
});

const recommendationCards = computed(() => {
  const list = [];
  const latest = activeAnalysis.value;
  if (typeOffre.value === 'duo' && activeDuoTab.value === 'couple') {
    const a1 = rawAnalysisConjoint1.value;
    const a2 = rawAnalysisConjoint2.value;
    const completed1 = isAnalysePublished(a1);
    const completed2 = isAnalysePublished(a2);
    if (!completed1 || !completed2) return list;
    const dateLabel = '—';
    const detailHtml = buildCoupleDetailHtml(a1, a2, duoMenage.value);
    list.push({
      id: 'duo-couple',
      title: 'Recommandations ménage (Couple)',
      description: 'Synthèse des recommandations du conjoint 1 et du conjoint 2, avec agrégation ménage.',
      dateLabel,
      status: 'termine',
      progress: 100,
      detailHtml,
    });
    return list;
  }
  if (!latest || !isAnalysisCompleted.value) return list;

  const id = latest.id ?? latest.pk ?? '1';
  const createdAt = latest.created_at ?? latest.date_creation ?? latest.createdAt;
  const dateLabel = formatDate(createdAt);

  const detailParts = [];
  // 1) Recommandations IA (markdown rendu en HTML pour un texte homogène)
  const iaText = latest.recommandation_ia_modifie || latest.recommandation_ia;
  if (iaText) {
    const rendered = renderMarkdownToHtml(iaText);
    detailParts.push(
      `<div class="reco-detail-section reco-rendered"><h4>Recommandations personnalisées</h4><div class="reco-rendered-body">${rendered}</div></div>`
    );
  }

  // 2) Tableau allocation selon horizon retraite (référentiel)
  const horizonHtml = buildHorizonAllocationTable(latest);
  if (horizonHtml) detailParts.push(horizonHtml);

  // 3) Tableau répartition épargne par catégorie
  const repartitionHtml = buildRepartitionEpargneTable(latest);
  if (repartitionHtml) detailParts.push(repartitionHtml);

  // 4) Contenu d'analyse libre éventuel
  if (latest.contenu_analyse) {
    detailParts.push(
      `<div class="reco-detail-section">${sanitizeHtml(String(latest.contenu_analyse))}</div>`
    );
  }

  const defaultDetail = getDefaultAnalysisDetail();
  const detailHtml = detailParts.length ? detailParts.join('') : defaultDetail;

  list.push({
    id: String(id),
    title: 'Analyse patrimoniale',
    description: 'Recommandations personnalisées selon votre profil et vos objectifs.',
    dateLabel,
    status: todoActions.value.length ? (todoProgress.value >= 100 ? 'termine' : todoProgress.value > 0 ? 'en_cours' : 'a_faire') : 'termine',
    progress: todoActions.value.length ? todoProgress.value : 100,
    detailHtml,
  });
  return list;
});

const isAnalysisCompleted = computed(() => {
  if (typeOffre.value === 'duo' && activeDuoTab.value === 'couple') {
    return isAnalysePublished(rawAnalysisConjoint1.value) && isAnalysePublished(rawAnalysisConjoint2.value);
  }
  return isAnalysePublished(activeAnalysis.value);
});

const hasSubmittedAnalysis = computed(() => {
  if (typeOffre.value === 'duo' && activeDuoTab.value === 'couple') {
    return Boolean(rawAnalysisConjoint1.value || rawAnalysisConjoint2.value);
  }
  if (typeOffre.value === 'duo') {
    return Boolean(activeAnalysis.value);
  }
  return Boolean(rawAnalysis.value);
});

const recoEmptyTitle = computed(() =>
  hasSubmittedAnalysis.value
    ? 'Analyse en cours de traitement'
    : 'Commencez par votre analyse patrimoniale'
);

const recoEmptyMessage = computed(() =>
  hasSubmittedAnalysis.value
    ? 'Votre dossier a bien été transmis. Nos équipes finalisent votre bilan ; les recommandations s’affichent ici dès publication. Notification par email sous environ 72 h.'
    : 'Les recommandations apparaissent une fois votre questionnaire soumis et votre analyse publiée. Comptez environ 72 h après soumission.'
);

const recoEmptyStep = computed(() => (hasSubmittedAnalysis.value ? 2 : 0));

const recoEmptyPrimaryTo = computed(() =>
  hasSubmittedAnalysis.value
    ? '/analyse-patrimoniale/analyses-sups'
    : '/analyse-patrimoniale/mon-analyse'
);

const recoEmptyPrimaryLabel = computed(() =>
  hasSubmittedAnalysis.value ? 'Voir l’état de mes résultats' : 'Continuer mon analyse'
);

function getDefaultAnalysisDetail() {
  return `
    <div class="reco-detail-section">
      <h4><strong>Cette semaine — 3 actions prioritaires</strong></h4>
      <p>1. Clarifier votre trésorerie de précaution (objectif : 3 à 6 mois de charges).</p>
      <p>2. Lister vos projets à 1–3 ans (montant, échéance, priorité).</p>
      <p>3. Préparer les questions à poser à votre conseiller pour arbitrer le plan.</p>
      <h4><strong>Horizon court (6 à 36 mois)</strong></h4>
      <p><em>Objectif :</em> sécurité et liquidité pour couvrir les imprévus.</p>
      <p>Épargne disponible, dépôts à terme, supports prudents adaptés à votre profil.</p>
      <h4><strong>Horizon moyen (3 à 10 ans)</strong></h4>
      <p><em>Objectif :</em> financer vos projets et valoriser le capital.</p>
      <p>Allocation progressive selon votre capacité d’épargne et votre tolérance au risque.</p>
      <h4><strong>Horizon long (10 ans et +)</strong></h4>
      <p><em>Objectif :</em> revenu de remplacement et transmission.</p>
      <p>Plan retraite et protection familiale à construire avec votre conseiller agréé.</p>
      <p><em>Les supports de marché réglementés sont recommandés uniquement après échange avec votre conseiller, selon votre profil de risque.</em></p>
    </div>
  `;
}

function redirectIfBilanCodeRequired(error) {
  if (error?.response?.data?.code === 'bilan_code_required') {
    router.push('/analyse-patrimoniale/analyses-sups');
    return true;
  }
  return false;
}

async function fetchAnalysisData() {
  loadingAnalysis.value = true;
  errorMessage.value = '';
  try {
    const response = await axios.get('/api/analyse-patrimoniale/?latest=true', {
      withCredentials: true,
    });
    const latest = normalizeLatestDetails(response.data);
    rawAnalysis.value = latest;
  } catch (error) {
    if (redirectIfBilanCodeRequired(error)) return;
    errorMessage.value = getErrorMessage(error).message;
  } finally {
    loadingAnalysis.value = false;
  }
}

async function fetchClientOffer() {
  try {
    if (!isAuthenticatedSession()) return;
    const { data } = await axios.get('/api/all-clients/', {
      withCredentials: true,
    });
    typeOffre.value = String(data?.type_offre || 'persona').toLowerCase();
  } catch (_) {
    typeOffre.value = 'persona';
  }
}

async function fetchLatestDuo(ordre) {
  const res = await axios.get('/api/analyse-patrimoniale/', {
    params: { latest: 'true', ordre_duo: ordre },
    withCredentials: true,
  });
  return normalizeLatestDetails(res.data);
}

async function fetchDuoMenage() {
  const res = await axios.get('/api/analyse-patrimoniale/duo-bilan/', {
    withCredentials: true,
  });
  duoMenage.value = res.data?.menage || null;
}

async function fetchTodos() {
  try {
    const { data } = await axios.get('/api/suivi-analyse/mes-actions/', {
      withCredentials: true,
    });
    todoActions.value = Array.isArray(data?.actions) ? data.actions : [];
  } catch (error) {
    // Statut "termine" par défaut (recommendationCards) si l'échéancier n'est pas disponible.
    todoActions.value = [];
  }
}

async function fetchRecommendations() {
  loadingAnalysis.value = true;
  errorMessage.value = '';
  try {
    await fetchClientOffer();
    if (typeOffre.value === 'duo') {
      const [a1, a2] = await Promise.all([fetchLatestDuo(1), fetchLatestDuo(2)]);
      rawAnalysisConjoint1.value = a1;
      rawAnalysisConjoint2.value = a2;
      // Précharger la synthèse ménage (facultatif) pour l’onglet Couple
      try { await fetchDuoMenage(); } catch (_) {}
    } else {
      await fetchAnalysisData();
    }
  } catch (error) {
    if (redirectIfBilanCodeRequired(error)) return;
    errorMessage.value = getErrorMessage(error).message;
  } finally {
    loadingAnalysis.value = false;
  }
}

onMounted(async () => {
  await syncEmployeProfileFromApi();
  fetchRecommendations();
  fetchTodos();
});

watch(activeDuoTab, () => {
  expandedId.value = null;
});
</script>

<style scoped>
.reco-page {
  min-height: 100%;
  font-family: var(--font-body);
  color: var(--client-text);
  padding-bottom: 3rem;
}

.reco-page__content {
  max-width: 800px;
  margin: 0 auto;
}

.reco-duo-tabs {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 0.25rem;
  margin: 0 0 1.25rem;
  background: var(--paper);
  border: 1px solid var(--client-line);
  border-radius: var(--radius-pill);
  max-width: 520px;
}

.reco-duo-tab {
  flex: 1 1 0;
  border: 0;
  background: transparent;
  color: var(--brand-navy);
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.55rem 0.75rem;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.reco-duo-tab:hover {
  background: var(--client-surface-card-hover);
}

.reco-duo-tab.active {
  background: var(--brand-navy);
  color: var(--paper);
}

.reco-duo-mini-date {
  font-weight: 500;
  font-size: 0.85em;
  color: var(--client-text-muted);
}

.reco-alert {
  margin-bottom: 1.5rem;
}

.reco-loading {
  text-align: center;
  padding: 3rem 1rem;
}

.reco-loading__spinner {
  width: 2.5rem;
  height: 2.5rem;
  margin: 0 auto 1rem;
  border: 3px solid var(--client-line);
  border-top-color: var(--brand-gold);
  border-radius: 50%;
  animation: reco-spin 0.8s linear infinite;
}

.reco-loading__text {
  font-size: var(--text-prose);
  color: var(--client-text-muted);
  margin: 0;
}

@keyframes reco-spin {
  to {
    transform: rotate(360deg);
  }
}

.reco-empty {
  text-align: center;
  padding: 1.5rem 1rem;
  background: var(--client-surface-card);
  border: 1px solid var(--client-line);
  border-radius: var(--client-radius-lg);
}

.reco-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.reco-card {
  background: var(--client-surface-card);
  border-radius: var(--client-radius-lg);
  border: 1px solid var(--client-line);
  padding: 1.5rem 1.75rem;
  transition: background 0.25s ease, border-color 0.25s ease;
  cursor: pointer;
}

.reco-card:hover {
  background: var(--client-surface-card-hover);
  border-color: var(--brand-gold);
}

.reco-card__header {
  margin-bottom: 1rem;
}

.reco-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.reco-card__title {
  font-family: var(--font-display);
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--brand-navy);
  margin: 0;
}

.reco-card__badge {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  padding: 0.25rem 0.65rem;
  border-radius: 9999px;
  border: 1px solid transparent;
}

.reco-card__badge--a_faire {
  background: var(--brand-gold-soft);
  color: var(--brand-brown);
  border-color: rgba(201, 162, 39, 0.28);
}

.reco-card__badge--en_cours {
  background: rgba(1, 38, 62, 0.08);
  color: var(--brand-navy);
  border-color: rgba(1, 38, 62, 0.18);
}

.reco-card__badge--termine {
  background: rgba(45, 134, 89, 0.12);
  color: var(--success);
  border-color: rgba(45, 134, 89, 0.22);
}

.reco-card__description {
  font-family: var(--font-body);
  font-size: var(--text-prose);
  color: var(--client-text-muted);
  margin: 0 0 0.5rem 0;
  line-height: 1.55;
}

.reco-card__date {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: var(--text-xs);
  color: var(--client-text-muted);
  margin: 0;
}

.reco-card__date-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  opacity: 0.85;
}

.reco-card__progress-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.reco-card__progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(84, 57, 41, 0.12);
  border-radius: 9999px;
  overflow: hidden;
}

.reco-card__progress-fill {
  height: 100%;
  background: var(--brand-gold);
  border-radius: 9999px;
  transition: width 0.3s ease;
}

.reco-card__progress-text {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--client-text-muted);
  min-width: 2.5rem;
}

.reco-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.reco-card__btn-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.reco-card__detail {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--client-line);
}

.reco-card__detail-inner {
  font-family: var(--font-body);
  font-size: var(--text-prose);
  color: var(--client-text);
  line-height: 1.6;
}

.reco-card__detail-inner :deep(.reco-detail-section) {
  margin-bottom: 1rem;
}

.reco-card__detail-inner :deep(h4) {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--brand-navy);
  margin: 0 0 0.5rem 0;
}

.reco-card__detail-inner :deep(p) {
  margin: 0 0 0.5rem 0;
}

.reco-card__detail-inner :deep(ul) {
  margin: 0.25rem 0 0.75rem 1.25rem;
  padding: 0;
}

.reco-card__detail-inner :deep(.reco-rendered-body) {
  margin-top: 0.5rem;
}

.reco-card__detail-inner :deep(.reco-rendered-heading) {
  font-size: 1.05rem;
  font-weight: 600;
  font-family: var(--font-display);
  color: var(--brand-navy);
  margin: 1em 0 0.5em 0;
}

.reco-card__detail-inner :deep(.reco-rendered-heading:first-child) {
  margin-top: 0;
}

.reco-card__detail-inner :deep(.reco-rendered-p) {
  margin: 0 0 0.6em 0;
  line-height: 1.6;
  color: var(--client-text);
}

.reco-card__detail-inner :deep(.reco-rendered-p strong) {
  color: var(--brand-navy);
}

.reco-card__detail-inner :deep(.reco-table-wrap) {
  overflow-x: auto;
  margin: 0.75rem 0 1rem 0;
  border: 1px solid var(--client-line);
  border-radius: var(--radius-field);
  background: var(--paper);
}

.reco-card__detail-inner :deep(.reco-table) {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-prose);
}

.reco-card__detail-inner :deep(.reco-table th),
.reco-card__detail-inner :deep(.reco-table td) {
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid var(--client-line);
}

.reco-card__detail-inner :deep(.reco-table th) {
  background: var(--client-surface-card);
  font-weight: 600;
  color: var(--brand-navy);
}

.reco-card__detail-inner :deep(.reco-table-total td) {
  font-weight: 600;
  background: var(--client-surface-card);
}

.reco-card__detail-inner :deep(.reco-table-cat) {
  font-weight: 500;
  color: var(--client-text);
}

.card-list-enter-active,
.card-list-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.card-list-enter-from,
.card-list-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

.expand-enter-active,
.expand-leave-active {
  transition: opacity 0.2s ease;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
}

@media (max-width: 480px) {
  .reco-card__actions {
    flex-direction: column;
  }

  .reco-card__actions .client-btn {
    width: 100%;
    min-height: 44px;
  }
}


</style>
